export interface SnapshotDiffRow {
  path: string;
  screen: unknown;
  api: unknown;
  database: unknown;
  status: 'same' | 'screen_changed' | 'api_db_mismatch' | 'not_comparable';
}

const isObject = (value: unknown): value is Record<string, unknown> => (
  value !== null && typeof value === 'object' && !Array.isArray(value)
);

const flatten = (value: unknown, prefix = '', out: Map<string, unknown> = new Map()): Map<string, unknown> => {
  if (Array.isArray(value)) {
    if (!value.length) out.set(prefix || '(root)', []);
    value.forEach((item, index) => flatten(item, `${prefix}[${index}]`, out));
  } else if (isObject(value)) {
    const entries = Object.entries(value);
    if (!entries.length) out.set(prefix || '(root)', {});
    entries.forEach(([key, item]) => flatten(item, prefix ? `${prefix}.${key}` : key, out));
  } else {
    out.set(prefix || '(root)', value);
  }
  return out;
};

const equal = (left: unknown, right: unknown) => JSON.stringify(left) === JSON.stringify(right);

export const buildSnapshotDiff = (screenDraft: unknown, lastApiResponse: unknown, databaseRecord: unknown): SnapshotDiffRow[] => {
  const screen = flatten(screenDraft);
  const api = flatten(lastApiResponse);
  const database = flatten(databaseRecord);
  const paths = Array.from(new Set([
    ...Array.from(screen.keys()),
    ...Array.from(api.keys()),
    ...Array.from(database.keys()),
  ])).sort();

  return paths.map(path => {
    const screenValue = screen.get(path);
    const apiValue = api.get(path);
    const databaseValue = database.get(path);
    let status: SnapshotDiffRow['status'] = 'same';
    if (databaseRecord === undefined || databaseRecord === null) status = 'not_comparable';
    else if (!equal(apiValue, databaseValue)) status = 'api_db_mismatch';
    else if (!equal(screenValue, apiValue)) status = 'screen_changed';
    return { path, screen: screenValue, api: apiValue, database: databaseValue, status };
  });
};
